import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Modal } from '../Modal';
import { Button } from '../Button';
import { feedbackService, FeedbackCategory } from '../../api/services';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CATEGORY_OPTIONS: { value: FeedbackCategory; label: string }[] = [
  { value: 'feature_request', label: 'Feature request' },
  { value: 'bug', label: 'Bug' },
  { value: 'other', label: 'Other' },
];

export function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState<FeedbackCategory>('feature_request');
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    if (loading) return;
    setMessage('');
    setCategory('feature_request');
    onClose();
  };

  const handleSubmit = async () => {
    if (!message.trim()) return;

    try {
      setLoading(true);
      await feedbackService.submit({ message: message.trim(), category });
      toast.success("Thanks! We've got your feedback.");
      handleClose();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to send feedback');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Send Feedback">
      <div className="space-y-4">
        <p className="text-[--clinqr-text-secondary] text-sm">
          Found a bug or have an idea? Let us know — this goes straight to the Clinqr team.
        </p>

        <div>
          <label className="block text-sm font-medium text-[--clinqr-text-primary] mb-2">
            Category
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as FeedbackCategory)}
            className="w-full h-10 px-3 rounded-lg border-2 border-[--clinqr-border] bg-[--clinqr-bg-subtle]
              focus:outline-none focus:border-[--clinqr-primary] focus:ring-2 focus:ring-[--clinqr-primary]/20
              text-[--clinqr-text-primary]"
          >
            {CATEGORY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-[--clinqr-text-primary] mb-2">
            Message
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Tell us what's on your mind..."
            maxLength={5000}
            className="w-full h-32 px-4 py-3 rounded-lg border-2 border-[--clinqr-border] bg-[--clinqr-bg-subtle]
              focus:outline-none focus:border-[--clinqr-primary] focus:ring-2 focus:ring-[--clinqr-primary]/20
              resize-none text-[--clinqr-text-primary] placeholder:text-[--clinqr-text-muted]"
          />
        </div>

        <div className="flex gap-3 justify-end">
          <Button onClick={handleClose} variant="secondary" disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading || !message.trim()}>
            {loading ? 'Sending...' : 'Send Feedback'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
